import React, { useState } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
  Typography,
  Tooltip
} from '@mui/material';
import {
  ThumbUp,
  ThumbDown,
  Share,
  ThumbUpOutlined,
  ThumbDownOutlined,
  ContentCopy,
  Check,
  AccessTime
} from '@mui/icons-material';
import LinkIcon from '@mui/icons-material/Link';
import { useHLSFeedback, ShareRequest, IssueCastPublishResponse, IssueCast, IssueCastChapter } from '../../../api/hlsApiClient';
import { generateEpisodeDescription } from '../../../api/apiClient';
import AIHeader from '../AIHeader';

export interface HLSActionsProps {
  contentId?: string;
  hlsUrl?: string;
  isStreamComplete?: boolean; // New prop to indicate if HLS stream is complete (VOD) or not (EVENT)
  websiteId?: string; // New: website context for publishing IssueCast
  durationSeconds?: number; // New: total runtime derived from player
  hideFeedback?: boolean; // New: hide thumbs up/down buttons
  hideShare?: boolean; // New: hide share button
  hidePublish?: boolean; // New: hide publish IssueCast button
  onIssueCastPublished?: () => void; // New: callback after successful IssueCast publish
  initialName?: string;
  initialDescription?: string;
  initialPublishDate?: string;
  chapters?: IssueCastChapter[]; // New: chapters for IssueCast
  articleIds?: string[]; // New: article IDs for AI generation context
  locationName?: string; // New: location name for IssueCast
  podcastType?: 'multiple_articles' | 'emergency'; // New: podcast type for IssueCast
  classification?: {
    incident_types?: string[];
    locations?: string[];
    severity_tags?: string[];
    timestamp?: string;
  };
}

const HLSActions: React.FC<HLSActionsProps> = ({
  contentId = "",
  hlsUrl = "",
  isStreamComplete = false,
  websiteId = "",
  durationSeconds = 0,
  hideFeedback = true,
  hideShare = true,
  hidePublish = false,
  onIssueCastPublished,
  initialName = "",
  initialDescription = "",
  initialPublishDate = new Date().toISOString(),
  chapters = [],
  articleIds = [],
  locationName = "Unknown Location",
  podcastType = 'multiple_articles',
  classification
}) => {
  // State for thumbs up/down
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

  // State for podcast details
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);

  // Update local state when props change
  React.useEffect(() => {
    setName(initialName);
  }, [initialName]);

  React.useEffect(() => {
    setDescription(initialDescription);
  }, [initialDescription]);

  // Helper to format date for datetime-local input
  const formatDateForInput = (dateStr: string) => {
    try {
      return new Date(dateStr).toISOString().slice(0, 16);
    } catch (e) {
      return new Date().toISOString().slice(0, 16);
    }
  };

  const [publishDate, setPublishDate] = useState(formatDateForInput(initialPublishDate));

  // State for share dialog
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDescription, setShareDescription] = useState('');
  const [shareTTL, setShareTTL] = useState<ShareRequest['ttl']>('1day');
  const [shareName, setShareName] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [isPermanentShare, setIsPermanentShare] = useState(false);

  // State for confirmation message
  const [shareConfirmation, setShareConfirmation] = useState({
    open: false,
    message: '',
  });

  // State for the shared URL section
  const [sharedUrlInfo, setSharedUrlInfo] = useState<{
    visible: boolean;
    url: string;
    expiresAt: string;
    recipientName?: string;
  }>({
    visible: false,
    url: '',
    expiresAt: '',
  });

  // State for URL copy feedback
  const [copied, setCopied] = useState(false);

  // Publish IssueCast state
  const [issueCastResult, setIssueCastResult] = useState<IssueCast | null>(null);
  const [issueCastResponseRaw, setIssueCastResponseRaw] = useState<IssueCastPublishResponse | null>(null);

  // AI Generation state
  const [isGenerating, setIsGenerating] = useState(false);

  // API hook (extended)
  const { sendFeedback, createShare, isLoading, publishIssueCast, publishLoading, publishError } = useHLSFeedback();

  const disabled = isLoading || publishLoading;

  const handleGeneratePodcastDescriptionClick = async () => {
    if (!websiteId) {
      alert("Website ID is missing. Cannot generate description.");
      return;
    }

    setIsGenerating(true);
    try {
      const response = await generateEpisodeDescription(websiteId, {
        name,
        publishDate: new Date(publishDate).toISOString(),
        locationName,
        chapters: chapters.map(c => ({
          title: c.title,
          startTime: c.startTime,
          runTimeSeconds: c.runTimeSeconds,
          linkToOriginal: c.linkToOriginal,
          script: c.script
        })),
        articleIds,
        hlsUrl
      });

      if (response.error) {
        alert(`Failed to generate description: ${response.error}`);
      } else if (response && response.description) {
        setDescription(response.description);
      }
    } catch (error) {
      console.error("Error generating description:", error);
      alert("Failed to generate description. Please try again.");
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle feedback (thumbs up/down)
  const handleFeedback = (type: 'up' | 'down') => {
    // Toggle feedback if already selected
    if (feedback === type) {
      sendFeedback(contentId, null);
      setFeedback(null);
    } else {
      sendFeedback(contentId, type);
      setFeedback(type);
    }
  };

  // Share dialog handlers
  const handleOpenShareDialog = () => {
    setShareDialogOpen(true);
  };

  const handleCloseShareDialog = () => {
    setShareDialogOpen(false);
    setShareDescription('');
    setShareTTL('1day');
    setShareName('');
    setShareEmail('');
    setEmailError('');
    setSendEmail(false); // Reset send email checkbox
    setIsPermanentShare(false); // Reset permanent share checkbox
  };

  const handleShareDescriptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShareDescription(event.target.value);
  };

  const handleShareTTLChange = (event: SelectChangeEvent) => {
    setShareTTL(event.target.value as ShareRequest['ttl']);
  };

  const handleShareNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShareName(event.target.value);
  };

  const handleShareEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const email = event.target.value;
    setShareEmail(email);

    // Validate email when changed
    validateEmail(email);
  };

  // Handle the send email checkbox change
  const handleSendEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSendEmail(event.target.checked);
  };

  const handlePermanentShareChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsPermanentShare(event.target.checked);
  };

  // Email validation function
  const validateEmail = (email: string) => {
    if (!email) {
      setEmailError('');
      return true;
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const isValid = emailRegex.test(email);

    if (!isValid) {
      setEmailError('Please enter a valid email address');
      return false;
    } else {
      setEmailError('');
      return true;
    }
  };

  // Handle confirmation message close
  const handleCloseConfirmation = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShareConfirmation({ ...shareConfirmation, open: false });
  };

  // Handle URL copy
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(sharedUrlInfo.url);
      setCopied(true);

      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleCreateShare = async () => {
    if (isPermanentShare) {
      const confirmPermanent = window.confirm("You are about to create a permanent, publicly accessible link. Anyone with this link can access the content indefinitely. Do you wish to proceed?");
      if (!confirmPermanent) return;
    }

    if (!isPermanentShare && shareEmail && !validateEmail(shareEmail)) {
      return;
    }

    try {
      const response = await createShare({
        contentId,
        message: shareDescription, // Always send the description (even for permanent shares)
        ttl: isPermanentShare ? 'permanent' : shareTTL,
        recipientName: isPermanentShare ? '' : shareName,
        recipientEmail: isPermanentShare ? '' : shareEmail,
        hlsUrlToCopy: hlsUrl,
        sendEmail: !isPermanentShare && sendEmail && !!shareEmail
      });

      handleCloseShareDialog();

      const shareUrl = `${process.env.REACT_APP_LISTENER_URL}/?id=${response.id}`;

      setShareConfirmation({
        open: true,
        message: isPermanentShare ? 'Permanent public link created successfully!' : `Content shared successfully${shareName ? ` with ${shareName}` : ''}!`,
      });

      setSharedUrlInfo({
        visible: true,
        url: shareUrl,
        expiresAt: isPermanentShare ? 'Permanent Link (No Expiration)' : response.expiresAt,
        recipientName: isPermanentShare ? undefined : shareName || undefined
      });

    } catch (error) {
      console.error('Error creating share:', error);
    }
  };

  const handlePublishIssueCast = async () => {
    if (!hlsUrl || !websiteId || !isStreamComplete || !durationSeconds) return;
    setIssueCastResult(null);
    setIssueCastResponseRaw(null);
    const request = {
      issueCastParams: {
        hlsUrl,
        publishDate: new Date(publishDate).toISOString(),
        websiteId,
        name,
        description,
        totalRunTime: durationSeconds,
        chapters,
        locationName,
        podcastType,
        classification
      },
    };
    console.debug('[HLSActions] publishIssueCast request', request);
    const resp = await publishIssueCast(request as any);
    setIssueCastResponseRaw(resp);
    if (resp.issueCast) {
      setIssueCastResult(resp.issueCast);
      if (onIssueCastPublished) {
        onIssueCastPublished();
      }
    }
  };

  const canPublish = !!hlsUrl && !!websiteId && isStreamComplete && (durationSeconds || 0) > 0 && !publishLoading;

  // Derive specific reasons why publishing is disabled
  const missingPublishReasons: string[] = [];
  if (!hlsUrl) missingPublishReasons.push('Missing HLS URL');
  if (!websiteId) missingPublishReasons.push('No website selected');
  if (!isStreamComplete) missingPublishReasons.push('Stream not complete yet');
  if (!(durationSeconds || 0)) missingPublishReasons.push('Duration unknown');
  // Note: publishLoading handled separately via spinner state

  // Get TTL display text
  const getTTLDisplayText = (ttl: string) => {
    switch (ttl) {
      case '1hour': return '1 Hour';
      case '1day': return '1 Day';
      case '1week': return '1 Week';
      case '1month': return '1 Month';
      default: return '1 Day';
    }
  };

  // Format expiration date
  const formatExpirationDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  return (
    <Box
      className="hls-actions"
      sx={{
        mt: 2,
        p: 2,
        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}
    >
      {/* Action Buttons */}
      <Grid container spacing={2} alignItems="center" justifyContent="center" maxWidth="lg">
        {!hideFeedback && (
          <>
            <Grid item>
              <Tooltip title={feedback === 'up' ? 'Remove like' : 'Like'}>
                <IconButton
                  aria-label={feedback === 'up' ? 'Remove like' : 'Like'}
                  onClick={() => handleFeedback('up')}
                  color={feedback === 'up' ? 'primary' : 'default'}
                  disabled={isLoading}
                  size="large"
                >
                  {feedback === 'up' ? <ThumbUp /> : <ThumbUpOutlined />}
                </IconButton>
              </Tooltip>
            </Grid>

            <Grid item>
              <Tooltip title={feedback === 'down' ? 'Remove dislike' : 'Dislike'}>
                <IconButton
                  aria-label={feedback === 'down' ? 'Remove dislike' : 'Dislike'}
                  onClick={() => handleFeedback('down')}
                  color={feedback === 'down' ? 'primary' : 'default'}
                  disabled={isLoading}
                  size="large"
                >
                  {feedback === 'down' ? <ThumbDown /> : <ThumbDownOutlined />}
                </IconButton>
              </Tooltip>
            </Grid>
          </>
        )}

        {!hideShare && (
          <Grid item>
            <Tooltip title="Share">
              <IconButton
                aria-label="Share"
                onClick={handleOpenShareDialog}
                color="primary"
                size="large"
                disabled={!isStreamComplete}
              >
                <Share />
              </IconButton>
            </Tooltip>
          </Grid>
        )}

        {!hidePublish && (
          <>
            <Grid item>
              <Button
                variant="contained"
                color="primary"
                onClick={handlePublishIssueCast}
                disabled={!canPublish}
                startIcon={publishLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
                size="small"
              >
                {publishLoading ? 'Saving…' : 'Save Podcast'}
              </Button>
            </Grid>
            <Grid item>
              <TextField
                label="Episode Name"
                variant="outlined"
                size="small"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </Grid>
            <Grid item>
              <TextField
                label="Publish Date"
                type="datetime-local"
                variant="outlined"
                size="small"
                value={publishDate}
                onChange={(e) => setPublishDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mb: '10px' }}>
              <AIHeader
                tooltip="Generate podcast description with AI"
                dialogTitle="Generate podcast description with AI"
                dialogDescription="This operation will use AI to Generate the podcast description."
                progressMessage="AI is finding generating the podcast description. This will take a few minutes..."
                onGenerateConfirm={handleGeneratePodcastDescriptionClick}
                isGenerating={isGenerating}
                setIsGenerating={setIsGenerating}
                generateButtonText="Generate"
                cancelButtonText="Cancel"
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={12} sx={{ width: '100%' }}>
              <TextField
                label="Description"
                variant="outlined"
                size="small"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={4}
                fullWidth
                sx={{
                  '& .MuiInputBase-input': {
                    resize: 'vertical',
                  }
                }}
              />
            </Grid>
          </>
        )}
      </Grid>

      {publishError && (
        <Alert severity="error" sx={{ mt: 2, width: '100%', maxWidth: 900 }}>
          Failed to publish IssueCast: {publishError}
        </Alert>
      )}

      {!publishError && !canPublish && !publishLoading && (
        <Alert severity="info" sx={{ mt: 2, width: '100%', maxWidth: 900 }}>
          Cannot publish yet: {missingPublishReasons.join(', ')}.
        </Alert>
      )}

      {issueCastResponseRaw && !publishError && (
        <Collapse in={!!issueCastResponseRaw} sx={{ width: '100%', mt: 2 }}>
          <Card variant="outlined" sx={{ maxWidth: 900, mx: 'auto' }}>
            <CardContent>
              <Typography variant="subtitle1" color="primary" gutterBottom>
                IssueCast Publish Result
              </Typography>
              {issueCastResult ? (
                <>
                  <Box sx={{ fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {/* Present key fields in structured way */}
                    <Typography variant="body2"><strong>ID:</strong> {issueCastResult.id}</Typography>
                    <Typography variant="body2"><strong>Publish Date:</strong> {issueCastResult.publishDate}</Typography>
                    {issueCastResult.publisher?.name && (
                      <Typography variant="body2"><strong>Publisher:</strong> {issueCastResult.publisher.name}</Typography>
                    )}
                    {issueCastResult.sponsor?.name && (
                      <Typography variant="body2"><strong>Sponsor:</strong> {issueCastResult.sponsor.name}</Typography>
                    )}
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      href={`${process.env.REACT_APP_ISSUECAST_URL}/?issueCastId=${issueCastResult.id}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Open IssueCast
                    </Button>
                  </Box>
                </>
              ) : (
                <Alert severity="warning">No IssueCast object returned.</Alert>
              )}
            </CardContent>
          </Card>
        </Collapse>
      )}

      {/* Shared URL Info Section */}
      <Collapse in={sharedUrlInfo.visible} sx={{ width: '100%', mt: 2 }}>
        <Card
          variant="outlined"
          sx={{
            bgcolor: (theme) =>
              alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.06),
            maxWidth: 'md',
            mx: 'auto'
          }}
        >
          <CardContent>
            <Typography variant="subtitle1" color="primary" gutterBottom>
              <Share fontSize="small" sx={{ verticalAlign: 'middle', mr: 1 }} />
              Share Created
              {sharedUrlInfo.recipientName && ` for ${sharedUrlInfo.recipientName}`}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 1,
                p: 1.5,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.divider}`
              }}
            >
              <Typography
                variant="body2"
                component="div"
                sx={{
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mr: 1,
                }}
              >
                {sharedUrlInfo.url}
              </Typography>
              <Tooltip title={copied ? "Copied!" : "Copy link"}>
                <IconButton
                  onClick={handleCopyUrl}
                  size="small"
                  color={copied ? "success" : "primary"}
                >
                  {copied ? <Check /> : <ContentCopy />}
                </IconButton>
              </Tooltip>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', mt: 1.5 }}>
              <AccessTime fontSize="small" sx={{ mr: 0.5, fontSize: '1rem' }} />
              Expires: {formatExpirationDate(sharedUrlInfo.expiresAt)}
            </Typography>
          </CardContent>
        </Card>
      </Collapse>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={handleCloseShareDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Share Content</DialogTitle>
        <DialogContent>
          <DialogContentText>
            With whom do you want to share this?
          </DialogContentText>

          <FormControlLabel
            control={<Checkbox checked={isPermanentShare} onChange={handlePermanentShareChange} color="primary" />}
            label="Create Permanent Public Share"
          />

          {isPermanentShare && (
            <Alert severity="info" sx={{ mt: 2 }}>
              This will create a permanent, publicly accessible link. No expiration or recipient details are required.
            </Alert>
          )}

          {/* Name field */}
          <TextField
            margin="dense"
            id="name"
            label="Their Name"
            type="text"
            fullWidth
            variant="outlined"
            value={shareName}
            onChange={handleShareNameChange}
            sx={{ mt: 2 }}
            disabled={isPermanentShare}
          />

          {/* Email field with validation */}
          <TextField
            margin="dense"
            id="email"
            label="Their Email"
            type="email"
            fullWidth
            variant="outlined"
            value={shareEmail}
            onChange={handleShareEmailChange}
            error={!!emailError}
            helperText={emailError}
            disabled={isPermanentShare}
          />

          {/* Send Email Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmail}
                onChange={handleSendEmailChange}
                name="sendEmail"
                color="primary"
                disabled={isPermanentShare || !shareEmail}
              />
            }
            label="Send email to recipient?"
            sx={{ mt: 1, display: 'block' }}
          />

          {/* Description field */}
          <TextField
            margin="dense"
            id="description"
            label={isPermanentShare ? "A description of this content" : "A message for them"}
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={shareDescription}
            onChange={handleShareDescriptionChange}
          />

          {/* TTL selection */}
          <FormControl fullWidth margin="normal" disabled={isPermanentShare}>
            <InputLabel id="ttl-select-label">Time to Live</InputLabel>
            <Select
              labelId="ttl-select-label"
              id="ttl-select"
              value={shareTTL}
              label="Time to Live"
              onChange={handleShareTTLChange}
            >
              <MenuItem value="1hour">1 Hour</MenuItem>
              <MenuItem value="1day">1 Day</MenuItem>
              <MenuItem value="1week">1 Week</MenuItem>
              <MenuItem value="1month">1 Month</MenuItem>
            </Select>
          </FormControl>
          {!isPermanentShare && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              This share will expire after {getTTLDisplayText(shareTTL)}.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseShareDialog}>Cancel</Button>
          <Button
            onClick={handleCreateShare}
            variant="contained"
            color="primary"
            disabled={isLoading || (!isPermanentShare && !shareDescription.trim()) || !!emailError}
            startIcon={isLoading ? <CircularProgress size={20} /> : null}
          >
            Create Share
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation message (without URL) */}
      <Snackbar
        open={shareConfirmation.open}
        autoHideDuration={4000}
        onClose={handleCloseConfirmation}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseConfirmation}
          severity="success"
          variant="filled"
        >
          {shareConfirmation.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HLSActions;